import { Box, Button, Divider } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { createMemberProfile } from 'src/shared/services/memberService';
import { getAllProfileGroups, getProfileGroupById } from 'src/shared/services/profileGroupService';
import { Member, MemberProfileData } from '../models/member';
import { MemberProfilePost } from '../models/memberProfilePost';
import { ProfileGroup } from '../models/profileGroup';

type MemberAddProfileType = {
  member: Member;
  callback: (profileItemId: string) => void;
  closeEdit: () => void;
};

export const MemberAddProfile = (props: MemberAddProfileType) => {
  const { member, callback, closeEdit } = props;
  const { t } = useTranslation(['member', 'common']);
  const [profileGroups, setProfileGroups] = useState<ProfileGroup[]>([]);
  const [selectedProfileGroup, setSelectedProfileGroup] = useState<ProfileGroup>();
  const [profileGroupWithItems, setProfileGroupWithItems] = useState<ProfileGroup>();
  const [selectedMemberProfile, setSelectedMemberProfile] = useState<MemberProfilePost>();

  const isMounted = useIsMounted();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getAllProfileGroups();
        if (isMounted()) {
          // På ett vänmedlemskap får bara de grupper väljas som är markerade som
          // tillgängliga för vänmedlemskap. Övriga hör till det ordinarie medlemskapet,
          // samma regel som styr vilka grupper formuläret renderar.
          const groups = member.parentMembership ? res.data.results.filter((x) => x.availableForExtraMembership === true) : res.data.results;

          setProfileGroups(groups);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      }
    };
    fetchData();
  }, [setProfileGroups, isMounted, member.parentMembership]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getProfileGroupById(selectedProfileGroup?.memberProfile_id);
        if (isMounted()) {
          // Filtrera bort redan sparade profiler för att inte spara dubbletter
          const result = res.data;
          const profilesAlreadyAdded: MemberProfileData[] = member.profileData ?? [];
          const filteredProfileItems = result.profileItems.filter((profileItem) => {
            return !profilesAlreadyAdded.some((addedProfileItem) => addedProfileItem.memberProfileItem_id === profileItem.memberProfileItem_id);
          });
          const filteredResult: ProfileGroup = {
            ...result,
            profileItems: filteredProfileItems,
          };
          setProfileGroupWithItems(filteredResult);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      }
    };
    if (selectedProfileGroup) {
      fetchData();
    }
  }, [selectedProfileGroup, isMounted, member.profileData]);

  const handleSelectProfileItem = async (val: SelectableItem) => {
    const newSelectedMemberProfile = {
      memberProfileItem_id: val.value,
      groupName: profileGroupWithItems?.title,
      itemName: val.label,
    } as unknown as MemberProfilePost;
    setSelectedMemberProfile(newSelectedMemberProfile);
    // callback(val);
  };

  const handleSaveProfile = async () => {
    if (selectedMemberProfile && member.member_id) {
      try {
        await createMemberProfile(member.member_id || 0, selectedMemberProfile);
        if (isMounted()) {
          toast.success(`${selectedMemberProfile.itemName} lades till`);
          callback(selectedMemberProfile.memberProfileItem_id.toString());
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      }
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ mb: 2 }}>
        <AutocompleteDropdown<SelectableItem>
          options={profileGroups.map((x) => {
            return { label: x.title, value: x.memberProfile_id.toString() };
          })}
          nameProp='label'
          keyProp='value'
          name='profile'
          callback={(val) => setSelectedProfileGroup(profileGroups.filter((x) => x.memberProfile_id.toString() === val?.value)[0])}
          label={t('member:editMember.profileGroup')}
          componentWidth='100%'
        />
      </Box>
      {profileGroupWithItems && (
        <Box sx={{ mb: 2 }}>
          <AutocompleteDropdown<SelectableItem>
            options={profileGroupWithItems.profileItems.map((x) => {
              return { label: x.name, value: x.memberProfileItem_id.toString() };
            })}
            nameProp='label'
            keyProp='value'
            name='profileItem'
            callback={(val) => val && handleSelectProfileItem(val)}
            label={t('member:editMember.profile')}
            componentWidth='100%'
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant='outlined' size='small' onClick={() => closeEdit()}>
          {t('common:button.cancel')}
        </Button>
        <Button
          variant='contained'
          size='small'
          disabled={!selectedMemberProfile}
          sx={{ ml: 1 }}
          disableElevation
          onClick={() => handleSaveProfile()}
        >
          {t('common:button.save')}
        </Button>
      </Box>
      <Divider sx={{ my: 2 }} />
    </Box>
  );
};
